import { Database } from "../../supabase.types";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import { logSupabaseException } from "@punchup/sentry";

type ComedianRow = Database["public"]["Tables"]["comedians"]["Row"];
type ComedianUpdate = Database["public"]["Tables"]["comedians"]["Update"];

/** Profile fields a caller can change. Used to report what actually changed. */
export type ComedianProfileField = "about" | "about_img";

export type UpdateComedianProfileParams = {
  comedianId: string;
  about?: string | null;
  aboutImg?: string | null;
};

export type UpdateComedianProfileResult = {
  comedian: ComedianRow;
  /**
   * Which fields actually changed value. A field passed in `params` but equal
   * to the stored value is NOT included — callers (e.g. audit logging) should
   * rely on this rather than on which params were present, because a caller
   * can send a field with its stored value.
   */
  changedFields: ComedianProfileField[];
};

/**
 * Updates the self-editable fields of a comedian's public profile: their bio
 * (`about`) and profile photo (`about_img`, a Cloudflare image id).
 *
 * Authorization is NOT performed here — callers must verify the user is
 * authorized to manage `comedianId` before invoking (e.g. via
 * `getIsUserAuthorizedToManageComedian`). This uses the service-role client, so
 * the write is not subject to RLS.
 *
 * Only the fields present in `params` are considered: omit `about` / `aboutImg`
 * to leave them untouched, or pass `null` to clear them. A provided field whose
 * value equals what is already stored is treated as unchanged — it is not
 * written and is excluded from `changedFields`. When nothing changes the write
 * is skipped entirely and the current row is returned.
 */
export async function updateComedianProfile({
  comedianId,
  about,
  aboutImg,
}: UpdateComedianProfileParams): Promise<UpdateComedianProfileResult> {
  const supabase = createSupabaseAdminClient();

  // Load the current row first so we can diff against it and only write/report
  // fields that genuinely changed.
  const { data: existing, error: fetchError } = await supabase
    .from("comedians")
    .select("*")
    .eq("id", comedianId)
    .single();

  if (fetchError) {
    logSupabaseException(fetchError, "Failed to load comedian profile");
    throw fetchError;
  }
  if (!existing) {
    throw new Error(`updateComedianProfile: comedian ${comedianId} not found`);
  }

  const update: ComedianUpdate = {};
  const changedFields: ComedianProfileField[] = [];
  if (about !== undefined && about !== existing.about) {
    update.about = about;
    changedFields.push("about");
  }
  if (aboutImg !== undefined && aboutImg !== existing.about_img) {
    update.about_img = aboutImg;
    changedFields.push("about_img");
  }

  if (changedFields.length === 0) {
    return { comedian: existing, changedFields };
  }

  const { data, error } = await supabase
    .from("comedians")
    .update(update)
    .eq("id", comedianId)
    .select("*")
    .single();

  if (error) {
    logSupabaseException(error, "Failed to update comedian profile");
    throw error;
  }
  if (!data) {
    throw new Error(`updateComedianProfile: comedian ${comedianId} not found`);
  }

  return { comedian: data, changedFields };
}
